/**
 * AI Configuration - Privacy & AI Controls Tab
 *
 * T016: Data Sharing, Sensitive Data Masking, Data Retention, User Oversight,
 * Smart Categorisation, Smart Insights, Budget Analysis, Unusual Expense Detection.
 * Receipt Scanning omitted (descoped).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useAiSettings, useSaveAiSettings } from '../../../hooks/useAiSettings';
import { setNavigationGuardState } from '../useNavigationGuardState';
import type { SaveAiSettingsInput } from '../../../types/ai-configuration';
import { supabase } from '../../../lib/supabase';
import {
  formatCalendarYearLabel,
  INSIGHT_TIME_RANGE_LAST_12_MONTHS,
  INSIGHT_TIME_RANGE_THIS_YEAR,
  resolveInsightTimeRangeSelection,
} from '../../../utils/insight-time-range';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

const DATA_RETENTION_OPTIONS: { value: string; label: string }[] = [
  { value: 'session', label: 'Session Only' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days (recommended)' },
  { value: '90', label: '90 days' },
  { value: 'until_cleared', label: 'Until Manually Cleared' },
];

export function PrivacyControlsTab(): React.ReactElement {
  const { data: settings, isLoading, error } = useAiSettings();
  const saveMutation = useSaveAiSettings();
  const [formData, setFormData] = useState<Partial<SaveAiSettingsInput>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [historyStatus, setHistoryStatus] = useState<{
    messageCount: number;
    oldestMessageDays: number | null;
    lastClearedAt: string | null;
  } | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [showClearHistoryConfirm, setShowClearHistoryConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (settings) {
      setFormData({
        dataSharingOptOut: settings.dataSharingOptOut,
        sensitiveDataMasking: settings.sensitiveDataMasking,
        dataRetentionDays: settings.dataRetentionDays,
        reviewCategorisationsBeforeApply: settings.reviewCategorisationsBeforeApply,
        requireBudgetApproval: settings.requireBudgetApproval,
        smartCategorisationEnabled: settings.smartCategorisationEnabled,
        smartCategorisationConfidenceThreshold: settings.smartCategorisationConfidenceThreshold,
        cacheAiResponses: settings.cacheAiResponses,
        batchProcessing: settings.batchProcessing,
        unusualExpenseDetectionEnabled: settings.unusualExpenseDetectionEnabled,
      });
    }
  }, [settings]);

  const isDirty = useMemo(() => {
    if (!settings) return false;
    return (
      formData.dataSharingOptOut !== settings.dataSharingOptOut ||
      formData.sensitiveDataMasking !== settings.sensitiveDataMasking ||
      formData.dataRetentionDays !== settings.dataRetentionDays ||
      formData.reviewCategorisationsBeforeApply !==
        settings.reviewCategorisationsBeforeApply ||
      formData.requireBudgetApproval !== settings.requireBudgetApproval ||
      formData.smartCategorisationEnabled !== settings.smartCategorisationEnabled ||
      formData.smartCategorisationConfidenceThreshold !==
        settings.smartCategorisationConfidenceThreshold ||
      formData.cacheAiResponses !== settings.cacheAiResponses ||
      formData.batchProcessing !== settings.batchProcessing ||
      formData.unusualExpenseDetectionEnabled !== settings.unusualExpenseDetectionEnabled
    );
  }, [settings, formData]);

  useEffect(() => {
    setNavigationGuardState(isDirty, () => {});
    return () => setNavigationGuardState(false, null);
  }, [isDirty]);

  // Fetch chat history status for the status line under AI Chat History Retention.
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setIsHistoryLoading(true);
        const headers = await getAuthHeaders();
        const response = await fetch('/api/chat/history/status', { headers });
        if (!response.ok) {
          throw new Error('Failed to load history status');
        }
        const json = await response.json();
        setHistoryStatus(json);
      } catch {
        setHistoryStatus(null);
      } finally {
        setIsHistoryLoading(false);
      }
    };

    fetchStatus().catch(() => undefined);
  }, []);

  const handleSave = useCallback(async () => {
    if (!isDirty) return;
    setSaveStatus('saving');
    try {
      await saveMutation.mutateAsync(formData as SaveAiSettingsInput);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  }, [isDirty, formData, saveMutation]);

  const toggle = (field: keyof SaveAiSettingsInput) => () =>
    setFormData((p) => ({ ...p, [field]: !(p[field] ?? (settings as any)?.[field]) }));

  const handleExportHistory = useCallback(async () => {
    try {
      setIsExporting(true);
      const headers = await getAuthHeaders();
      const response = await fetch('/api/chat/history/export', { headers });
      if (!response.ok) throw new Error('Export failed');
      const json = await response.json();
      const messages = json.messages ?? [];
      const blob = new Blob([JSON.stringify({ messages }, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-chat-history-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Silent fail or toast in future
    } finally {
      setIsExporting(false);
    }
  }, []);

  const handleClearHistory = useCallback(async () => {
    setShowClearHistoryConfirm(false);
    try {
      setIsHistoryLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch('/api/chat/history', {
        method: 'DELETE',
        headers,
      });
      if (!response.ok && response.status !== 204) {
        throw new Error('Failed to clear history');
      }
      // Refresh status after clear.
      const statusRes = await fetch('/api/chat/history/status', { headers });
      if (statusRes.ok) {
        const json = await statusRes.json();
        setHistoryStatus(json);
      }
    } catch {
      // Non-fatal: UI will simply not update the status line.
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <p className="text-red-800 dark:text-red-200">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-content space-y-4 pt-6">
          <h3 className="heading-card">Privacy Controls</h3>
          {[
            {
              field: 'dataSharingOptOut' as const,
              title: 'Data Sharing Opt-out',
              desc: 'Opt out of sharing anonymised usage data with Money Mojo',
            },
            {
              field: 'sensitiveDataMasking' as const,
              title: 'Sensitive Data Masking',
              desc: 'Replace account numbers and identifiers before sending to AI',
            },
          ].map(({ field, title, desc }) => (
            <div
              key={field}
              className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >
              <div>
                <p className="heading-list-item">{title}</p>
                <p className="text-secondary">{desc}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData[field] ?? (settings as any)[field]}
                onClick={toggle(field)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  (formData[field] ?? (settings as any)[field])
                    ? 'bg-primary-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    (formData[field] ?? (settings as any)[field])
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}

          <div className="pt-3 space-y-2">
            <label className="heading-list-item block">
              AI Chat History Retention
            </label>
            <p className="text-secondary">
              Controls how long AI chat conversations are kept for better context. Shorter periods
              increase privacy but may limit follow-up answers.
            </p>
            <div className="mt-1 flex items-center gap-3">
              <select
                value={
                  formData.dataRetentionDays ?? settings.dataRetentionDays ?? ''
                    ? String(formData.dataRetentionDays ?? settings.dataRetentionDays)
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((p) => ({
                    ...p,
                    // Phase 3 will introduce dedicated chat history fields; for now we continue
                    // to map to dataRetentionDays so contracts remain green.
                    dataRetentionDays:
                      v === '' || v === 'session' || v === 'until_cleared' ? null : Number(v),
                  }));
                }}
                className="input input-select w-full max-w-xs"
                aria-label="AI Chat History Retention"
              >
                {DATA_RETENTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleExportHistory}
                disabled={isExporting || isHistoryLoading}
              >
                {isExporting ? 'Exporting…' : 'Export (JSON)'}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowClearHistoryConfirm(true)}
                disabled={isHistoryLoading}
              >
                Clear History
              </button>
            </div>
            <div className="flex items-center justify-between text-caption mt-1">
              <p>
                {isHistoryLoading
                  ? 'Loading chat history status…'
                  : historyStatus
                    ? `Current history: ${historyStatus.messageCount} messages${
                        historyStatus.oldestMessageDays != null
                          ? ` · Oldest: ${historyStatus.oldestMessageDays} days ago`
                          : ''
                      }`
                    : 'Current history information is not available.'}
              </p>
            </div>
            <p className="text-caption mt-1">
              {historyStatus?.lastClearedAt
                ? `Last manually cleared: ${new Date(
                    historyStatus.lastClearedAt
                  ).toLocaleDateString()}`
                : 'Last manually cleared: Never'}
            </p>
          </div>

        </div>
      </div>

      <div className="card">
        <div className="card-content space-y-4 pt-6">
          <h3 className="heading-card mb-1">AI Feature Controls</h3>
          <p className="text-secondary mb-3">
            Configure how AI auto-categorises transactions, caches responses, batches processing, and flags unusual
            spending while keeping you in control of final decisions.
          </p>
          {[
            {
              field: 'smartCategorisationEnabled' as const,
              title: 'Smart Categorisation',
              desc: 'Only auto-categorise transactions when AI is confident enough. Lower-confidence items stay for manual review.',
            },
            {
              field: 'cacheAiResponses' as const,
              title: 'Cache AI Responses',
              desc: 'Reduce API calls by caching similar requests.',
            },
            {
              field: 'batchProcessing' as const,
              title: 'Batch Processing',
              desc: 'Process multiple transactions in a single AI request.',
            },
            {
              field: 'unusualExpenseDetectionEnabled' as const,
              title: 'Unusual Expense Detection',
              desc: 'Alert on suspicious or unusual spending patterns once enough transaction history is available.',
            },
          ].map(({ field, title, desc }) => (
            <div
              key={field}
              className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-gray-700 last:border-0"
            >
              <div>
                <p className="heading-list-item">{title}</p>
                {desc && <p className="text-secondary">{desc}</p>}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={formData[field] ?? (settings as any)[field]}
                onClick={toggle(field)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  (formData[field] ?? (settings as any)[field])
                    ? 'bg-primary-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    (formData[field] ?? (settings as any)[field])
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}

          {(formData.smartCategorisationEnabled ?? settings.smartCategorisationEnabled) && (
            <div className="pt-3">
              <label className="heading-list-item block mb-2">Confidence Threshold (%)</label>
              <input
                type="number"
                min={50}
                max={100}
                value={
                  formData.smartCategorisationConfidenceThreshold ??
                  settings.smartCategorisationConfidenceThreshold
                }
                onChange={(e) =>
                  setFormData((p) => ({
                    ...p,
                    smartCategorisationConfidenceThreshold: parseInt(e.target.value, 10),
                  }))
                }
                className="input w-full max-w-xs"
                aria-label="Smart Categorisation confidence threshold"
              />
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-content space-y-4 pt-6">
          <h3 className="heading-card mb-1">Insight Generation Settings</h3>
          <p className="text-secondary mb-2">
            Control how often AI refreshes your insights and how much history it analyses. Longer
            time ranges and more frequent refreshes provide deeper insights but may use more AI
            requests.
          </p>

          <div className="space-y-1">
            <label className="heading-list-item block">Insight Refresh Frequency</label>
            <p className="text-secondary">
              How often AI automatically refreshes your insights.
            </p>
            <select
              value={formData.insightRefreshFrequency ?? settings.insightRefreshFrequency ?? 'daily'}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  insightRefreshFrequency: e.target.value as any,
                }))
              }
              className="input input-select w-full max-w-xs mt-1"
              aria-label="Insight Refresh Frequency"
            >
              <option value="manual">Manual only</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <p className="text-caption mt-1">
              {(() => {
                const freq = formData.insightRefreshFrequency ?? settings.insightRefreshFrequency ?? 'daily';
                if (freq === 'manual') {
                  return 'Manual only · 0 automatic AI requests/month · $0 (user-triggered only).';
                }
                if (freq === 'weekly') {
                  return 'Weekly · ~4 AI refreshes/month · approx $0.50–$1 based on GPT-4 usage.';
                }
                return 'Daily (recommended) · ~30 AI refreshes/month · approx $3–$5 based on GPT-4 usage.';
              })()}
            </p>
          </div>

          <div className="space-y-1 pt-3 border-t border-gray-200 dark:border-gray-700">
            <label className="heading-list-item block">Insight Time Range</label>
            <p className="text-secondary">
              How much historical data AI uses when generating insights.
            </p>
            <select
              value={String(
                resolveInsightTimeRangeSelection(
                  formData.insightTimeRangeDays ?? settings.insightTimeRangeDays ?? 90,
                  formData.insightCustomStartDate ?? settings.insightCustomStartDate,
                  formData.insightCustomEndDate ?? settings.insightCustomEndDate
                )
              )}
              onChange={(e) =>
                setFormData((p) => ({
                  ...p,
                  insightTimeRangeDays: Number(e.target.value),
                }))
              }
              className="input input-select w-full max-w-xs mt-1"
              aria-label="Insight Time Range"
            >
              <option value="7">This Week / Last Week (~7 days)</option>
              <option value="30">This Month / Last Month (~30 days)</option>
              <option value="90">Last 3 months (recommended)</option>
              <option value="180">Last 6 months</option>
              <option value={INSIGHT_TIME_RANGE_LAST_12_MONTHS}>Last 12 months</option>
              <option value={INSIGHT_TIME_RANGE_THIS_YEAR}>{formatCalendarYearLabel()}</option>
            </select>
            <p className="text-caption mt-1">
              Longer ranges detect deeper patterns but may require more historical transaction data.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saveStatus === 'saving'}
          data-loading={saveStatus === 'saving' ? 'true' : undefined}
          className="btn btn-primary btn-md flex items-center gap-2"
        >
          {saveStatus === 'saving' && <Loader2 className="h-4 w-4 animate-spin" />}
          {saveStatus === 'saved' && <CheckCircle className="h-4 w-4" />}
          {saveStatus === 'error' && <AlertCircle className="h-4 w-4" />}
          {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved' : 'Save Changes'}
        </button>
      </div>

      {showClearHistoryConfirm && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="card max-w-md w-full mx-4">
            <div className="card-content p-6 space-y-4">
              <h2 className="heading-component">Clear AI chat history?</h2>
              <p className="text-secondary">
                This will permanently delete your stored AI chat conversations. Insights that rely
                on past chats may become less accurate until new history is created.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowClearHistoryConfirm(false)}
                  disabled={isHistoryLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-destructive btn-sm"
                  onClick={handleClearHistory}
                  disabled={isHistoryLoading}
                >
                  {isHistoryLoading ? 'Clearing…' : 'Clear History'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
